package com.evoapps.evoshift.domain.usecase

import com.evoapps.evoshift.domain.model.DayTemplate
import com.evoapps.evoshift.domain.model.ShiftTemplate
import com.evoapps.evoshift.domain.repository.WorkplaceRepository

/**
 * Opening hours chosen in onboarding (SH-38). Days are ISO (1 = Monday .. 7 = Sunday), times are
 * minutes after midnight; [closesAtMinutes] may be 1440 (midnight), and must be after opening.
 */
data class OpeningHours(
    val openDays: Set<Int> = (1..6).toSet(),
    val opensAtMinutes: Int = 8 * 60,
    val closesAtMinutes: Int = 20 * 60
) {
    val isValid: Boolean
        get() = openDays.isNotEmpty() && openDays.all { it in 1..7 } &&
            opensAtMinutes in 0 until MINUTES_PER_DAY && closesAtMinutes in (opensAtMinutes + 1)..MINUTES_PER_DAY

    /**
     * The shifts that cover the day: one shift up to [MAX_SINGLE_SHIFT_MINUTES], otherwise a
     * morning and an evening shift split at the half hour nearest the middle.
     */
    fun starterShifts(): List<StarterShift> {
        if (!isValid) return emptyList()
        val span = closesAtMinutes - opensAtMinutes
        if (span <= MAX_SINGLE_SHIFT_MINUTES) {
            return listOf(StarterShift(StarterShiftKind.FULL_DAY, opensAtMinutes, closesAtMinutes))
        }
        val middle = opensAtMinutes + ((span / 2 + 15) / 30) * 30
        return listOf(
            StarterShift(StarterShiftKind.MORNING, opensAtMinutes, middle),
            StarterShift(StarterShiftKind.EVENING, middle, closesAtMinutes)
        )
    }

    companion object {
        const val MINUTES_PER_DAY = 24 * 60
        const val MAX_SINGLE_SHIFT_MINUTES = 9 * 60

        /** "08:30" for 510; midnight (0 or 1440) is "00:00" (a shift ending then ends next day). */
        fun clock(minutes: Int): String {
            val m = minutes % MINUTES_PER_DAY
            return "${(m / 60).toString().padStart(2, '0')}:${(m % 60).toString().padStart(2, '0')}"
        }
    }
}

enum class StarterShiftKind { FULL_DAY, MORNING, EVENING }

data class StarterShift(val kind: StarterShiftKind, val startMinutes: Int, val endMinutes: Int) {
    val startTime: String get() = OpeningHours.clock(startMinutes)
    val endTime: String get() = OpeningHours.clock(endMinutes)
}

/** Names for what [CreateStarterScheduleUseCase] creates, in the owner's language (from the UI). */
data class StarterScheduleNames(
    val fullDay: String,
    val morning: String,
    val evening: String,
    val dayTemplate: String
) {
    fun of(kind: StarterShiftKind): String = when (kind) {
        StarterShiftKind.FULL_DAY -> fullDay
        StarterShiftKind.MORNING -> morning
        StarterShiftKind.EVENING -> evening
    }
}

/** What [CreateStarterScheduleUseCase] created: the shift templates (with their ids) and the day template. */
data class StarterSchedule(val shiftTemplates: List<ShiftTemplate>, val dayTemplateId: String) {
    val shiftTemplateIds: List<String> get() = shiftTemplates.map { it.id }
}

/**
 * Turns the opening hours of a new workplace into its first schedule skeleton (SH-38): shift
 * templates covering the day, one day template with them, and that day template on every open
 * day of the weekly skeleton - the same documents the skeleton editor creates by hand, so the
 * owner can generate a first roster right away and refine it later.
 */
class CreateStarterScheduleUseCase(private val workplaceRepository: WorkplaceRepository) {
    suspend operator fun invoke(
        workplaceId: String,
        hours: OpeningHours,
        names: StarterScheduleNames
    ): Result<StarterSchedule> = runCatching {
        require(hours.isValid) { "Opening hours must close after they open on at least one day" }
        val templates = hours.starterShifts().map { shift ->
            val template = ShiftTemplate(
                id = "",
                name = names.of(shift.kind),
                startTime = shift.startTime,
                endTime = shift.endTime,
                role = null,
                skills = emptyList()
            )
            template.copy(id = workplaceRepository.createShiftTemplate(workplaceId, template).getOrThrow())
        }
        val shiftIds = templates.map { it.id }
        val dayTemplateId = workplaceRepository.createDayTemplate(
            workplaceId,
            DayTemplate(id = "", name = names.dayTemplate, shiftTemplateIds = shiftIds)
        ).getOrThrow()
        val skeleton = workplaceRepository.getWorkplace(workplaceId).getOrThrow().settings.defaultWeeklySkeleton
        val updated: Map<Int, String?> = skeleton + hours.openDays.associateWith { dayTemplateId }
        workplaceRepository.updateWeeklySkeleton(workplaceId, updated).getOrThrow()
        StarterSchedule(templates, dayTemplateId)
    }
}
